import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function SmokeHeatAlarmPage() {
  useEffect(() => {
    // Set page title and meta description
    document.title = 'Smoke & Heat Alarm Installation Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional smoke and heat detector installation in Watford. Fire safety alarms for homes & businesses. NICEIC approved. Call 020 3198 1818.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Professional smoke and heat detector installation in Watford. Fire safety alarms for homes & businesses. NICEIC approved. Call 020 3198 1818.';
      document.head.appendChild(meta);
    }

    // Add LocalBusiness JSON-LD schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/safety-compliance/smoke-heat-alarm",
      "telephone": "+442031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      },
      "serviceType": "Smoke & Heat Alarm Installation",
      "priceRange": "££",
      "openingHours": "Mo-Su 00:00-23:59"
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const areasServed = [
    'Watford Town Centre',
    'Oxhey',
    'Bushey',
    'Croxley Green',
    'Abbots Langley',
    'Rickmansworth',
    'Garston',
    'North Watford',
    'West Watford',
    'Carpenders Park',
    'Leavesden',
    'Nascot Wood',
    'Holywell',
    'Meriden',
    'Cassiobury'
  ];

  const whatsIncluded = [
    {
      icon: '🔥',
      title: 'Smoke Alarm Installation',
      description: 'Optical or ionisation smoke alarms on every floor as required by UK law'
    },
    {
      icon: '🌡️',
      title: 'Heat Alarm Installation',
      description: 'Heat detectors for kitchens and areas where smoke alarms may false trigger'
    },
    {
      icon: '🔗',
      title: 'Interlinked Systems',
      description: 'Wireless or hardwired interconnected alarms for whole-home protection'
    },
    {
      icon: '🔋',
      title: 'Power Options',
      description: 'Mains-powered with battery backup or long-life sealed battery units'
    },
    {
      icon: '✅',
      title: 'Compliance Testing',
      description: 'Full testing and certification to meet building regulations and landlord requirements'
    },
    {
      icon: '📱',
      title: 'Smart Options',
      description: 'WiFi-enabled alarms with smartphone alerts and remote monitoring available'
    }
  ];

  const benefits = [
    {
      icon: '👨‍👩‍👧‍👦',
      title: 'Family Safety',
      description: 'Early warning system to protect your loved ones from fire hazards'
    },
    {
      icon: '⚖️',
      title: 'Legal Compliance',
      description: 'Meet building regulations and landlord legal requirements'
    },
    {
      icon: '🏠',
      title: 'Property Protection',
      description: 'Minimize fire damage with early detection and rapid response'
    },
    {
      icon: '💼',
      title: 'Insurance Valid',
      description: 'Maintain valid home insurance with properly installed alarms'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628]"></div>
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://public.readdy.ai/ai/img_res/dfd6588a8b1728049e5960738f9fcfc1.jpg" 
            alt="Smoke & Heat Alarm Installation Background" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">🏆 NICEIC Approved</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">📍 Watford</span>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Smoke & Heat Alarm Installation<br />
            <span className="text-[#D4AF37]">in Watford</span>
          </h1>
          
          <p className="text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed">
            Professional smoke and heat alarm installation for homes and rental properties. Interlinked systems, compliance testing, and certification by NICEIC approved electricians.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <a 
              href="tel:02031981818"
              className="bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#C5A028] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link 
              to="/#contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Complete Alarm Installation Service</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional smoke and heat alarm systems for maximum protection
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whatsIncluded.map((item, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#0A1628] to-[#1a2942] rounded-lg mb-6 text-3xl">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Why Install Smoke & Heat Alarms?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Essential fire safety protection for every home
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-xl hover:bg-gray-50 transition-all duration-300"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-full mb-6 text-4xl mx-auto shadow-lg">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Beyond Electrical?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Watford's trusted experts for smoke and heat alarm installation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-shield-check-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">NICEIC Approved</h3>
              <p className="text-gray-300 text-sm">Professional certified installation</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-time-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Same-Day Service</h3>
              <p className="text-gray-300 text-sm">Fast installation when you need it</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-file-list-3-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Full Certification</h3>
              <p className="text-gray-300 text-sm">Compliance documentation provided</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-customer-service-2-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Expert Advice</h3>
              <p className="text-gray-300 text-sm">Best alarm system for your property</p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 flex items-center justify-center bg-[#D4AF37] rounded-full text-4xl flex-shrink-0">
                🏆
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">Fire Safety Specialists</h3>
                <p className="text-gray-300 leading-relaxed">
                  Our NICEIC approved electricians are experts in fire alarm systems. We'll design and install the perfect smoke and heat alarm setup for your property, ensuring full compliance and maximum protection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Areas We Cover</h2>
            <p className="text-xl text-gray-600">
              Smoke and heat alarm installation across Watford and surrounding areas
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {areasServed.map((area, index) => (
              <span 
                key={index}
                className="px-6 py-3 bg-white rounded-full text-[#0A1628] font-medium border border-gray-200 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Need Smoke & Heat Alarms Installed?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Protect your home and family with professional alarm installation from NICEIC approved electricians in Watford.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a 
              href="tel:02031981818"
              className="bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#C5A028] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link 
              to="/#contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap"
            >
              Request Callback
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-300">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>Same-Day Service</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>NICEIC Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>Full Certification</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}